import {
  Button,
  cn,
  CopyButton,
  EmptyState,
  Glass,
  Input,
  LiveIndicator,
  Select,
  Spinner,
  toast,
  Tooltip,
} from "@gotts.ai/ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AccountIdenticon } from "../components/AccountIdenticon";
import { AnimatedBalance } from "../components/AnimatedBalance";
import { ProtocolBadge } from "../components/ProtocolBadge";
import { useDeployment, useEvmBackend } from "../context";
import { useAddressResolver } from "../hooks/useAddressResolver";
import { useContractEvents } from "../hooks/useContractEvents";
import {
  FEE_MODULE_ABI,
  MOCK_ERC20_ABI,
  STATE_VIEW_ABI,
  V2_PAIR_ABI,
  V2_ROUTER_ABI,
  V3_FACTORY_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
  V3_POOL_ABI,
  V3_SWAP_ROUTER_ABI,
  VAULT_CORE_ABI,
  VAULT_FACTORY_ABI,
  WETH9_ABI,
} from "../lib/abis";

import type { DecodedEvent } from "../hooks/useContractEvents";

// ---------------------------------------------------------------------------
// Build ABIs and address-to-name mapping from deployment
// ---------------------------------------------------------------------------

function useEventInfra() {
  const { deployment } = useDeployment();

  const abis = useMemo(
    () => ({
      MockERC20: MOCK_ERC20_ABI as readonly unknown[],
      V2Pair: V2_PAIR_ABI as readonly unknown[],
      V2Router: V2_ROUTER_ABI as readonly unknown[],
      V3Factory: V3_FACTORY_ABI as readonly unknown[],
      V3Pool: V3_POOL_ABI as readonly unknown[],
      V3NftPositionManager: V3_NFT_POSITION_MANAGER_ABI as readonly unknown[],
      V3SwapRouter: V3_SWAP_ROUTER_ABI as readonly unknown[],
      WETH9: WETH9_ABI as readonly unknown[],
      StateView: STATE_VIEW_ABI as readonly unknown[],
      VaultFactory: VAULT_FACTORY_ABI as readonly unknown[],
      VaultCore: VAULT_CORE_ABI as readonly unknown[],
      FeeModule: FEE_MODULE_ABI as readonly unknown[],
    }),
    [],
  );

  // addressNames map used by useContractEvents for event classification.
  // Preserves the "V2:factory" prefix format needed by classifyProtocol().
  const addressNames = useMemo(() => {
    if (!deployment) return {};
    const map: Record<string, string> = {};

    for (const [sym, addr] of Object.entries(deployment.tokens ?? {})) {
      map[String(addr)] = sym.toUpperCase();
    }
    for (const [name, addr] of Object.entries(deployment.v2)) {
      map[String(addr)] = `V2:${name}`;
    }
    for (const [name, addr] of Object.entries(deployment.v3)) {
      map[String(addr)] = `V3:${name}`;
    }
    for (const [name, addr] of Object.entries(deployment.v4)) {
      map[String(addr)] = `V4:${name}`;
    }
    for (const [name, addr] of Object.entries(deployment.foundation)) {
      map[String(addr)] = `Foundation:${name}`;
    }
    for (const [name, addr] of Object.entries(deployment.uniswapX)) {
      map[String(addr)] = `UniswapX:${name}`;
    }
    for (const [name, addr] of Object.entries(deployment.erc8004)) {
      map[String(addr)] = `ERC-8004:${name}`;
    }
    if (deployment.vault) {
      for (const [name, addr] of Object.entries(deployment.vault)) {
        map[String(addr)] = `Vault:${name}`;
      }
    }

    return map;
  }, [deployment]);

  return { abis, addressNames };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function classifyProtocol(contractName: string | null): string {
  if (!contractName) return "Other";
  if (contractName.startsWith("V2:")) return "V2";
  if (contractName.startsWith("V3:")) return "V3";
  if (contractName.startsWith("V4:")) return "V4";
  if (contractName.startsWith("ERC-8004:")) return "ERC-8004";
  if (contractName.startsWith("UniswapX:")) return "UniswapX";
  if (contractName.startsWith("Vault:")) return "Vault";
  const upper = contractName.toUpperCase();
  if (["WETH", "USDC", "USDT", "DAI", "WBTC"].includes(upper)) return "ERC20";
  return "Other";
}

/** Classify event name into a semantic type for badge coloring. */
function classifyEventType(eventName: string | null): string {
  if (!eventName) return "Other";
  const lower = eventName.toLowerCase();
  if (lower === "transfer") return "Transfer";
  if (lower === "approval" || lower === "approvalforall") return "Approval";
  if (lower.includes("swap")) return "Swap";
  if (lower === "deposit") return "Deposit";
  if (lower === "withdrawal" || lower === "withdraw") return "Withdrawal";
  if (lower === "mint" || lower === "increaseliquidity") return "Mint";
  if (lower === "burn" || lower === "decreaseliquidity") return "Burn";
  if (lower === "sync") return "Sync";
  if (lower === "poolcreated" || lower === "paircreated") return "PoolCreated";
  if (lower === "vaultcreated") return "VaultCreated";
  if (lower === "profitreported" || lower === "feesreported")
    return "FeeReport";
  if (lower === "agentdeposit") return "Deposit";
  if (lower === "agentwithdraw") return "Withdrawal";
  return "Other";
}

function formatTimestamp(ts: number): string {
  if (!ts) return "--";
  return new Date(ts * 1000).toLocaleTimeString("en-US", { hour12: false });
}

/** Format a BigInt value, adding a human-readable approximation for large values (>1e15). */
function formatBigIntValue(v: bigint): string {
  const raw = v.toString();
  const abs = v < 0n ? -v : v;
  if (abs > 1_000_000_000_000_000n) {
    const num = Number(v) / 1e18;
    const formatted = num.toLocaleString("en-US", { maximumFractionDigits: 6 });
    return `${raw} (~${formatted})`;
  }
  return raw;
}

/** Semantic event name color class (matching TransactionLog pattern). */
function eventColorClass(eventName: string): string {
  const lower = eventName.toLowerCase();
  if (lower === "transfer") return "text-blue-400";
  if (lower === "approval" || lower === "approvalforall")
    return "text-amber-400";
  if (lower === "deposit" || lower === "mint" || lower === "increaseliquidity")
    return "text-green-400";
  if (lower.includes("swap")) return "text-purple-400";
  if (
    lower === "withdrawal" ||
    lower === "withdraw" ||
    lower === "burn" ||
    lower === "decreaseliquidity"
  )
    return "text-teal-400";
  if (lower === "sync") return "text-cyan-400";
  if (lower === "poolcreated" || lower === "paircreated") return "text-gold";
  if (lower === "vaultcreated") return "text-pink-400";
  if (lower === "profitreported" || lower === "feesreported")
    return "text-fuchsia-400";
  if (lower === "agentdeposit") return "text-green-400";
  if (lower === "agentwithdraw") return "text-teal-400";
  if (lower === "vaultfeesinitialized") return "text-pink-300";
  return "text-text-dim";
}

// Protocol accent colors for left border strips (matching TransactionLog)
const PROTOCOL_ACCENT: Record<string, string> = {
  V2: "#4ade80",
  V3: "#60a5fa",
  V4: "#a855f7",
  ERC20: "#fbbf24",
  "ERC-8004": "#fb923c",
  UniswapX: "#facc15",
  Vault: "#10b981",
};

// Event type distribution bar colors
const EVENT_TYPE_COLORS: Record<string, string> = {
  Transfer: "bg-blue-500",
  Swap: "bg-purple-500",
  Approval: "bg-amber-500",
  Deposit: "bg-green-500",
  Withdrawal: "bg-teal-500",
  Mint: "bg-emerald-500",
  Burn: "bg-rose-500",
  Sync: "bg-cyan-500",
  PoolCreated: "bg-yellow-500",
  VaultCreated: "bg-emerald-500",
  FeeReport: "bg-lime-500",
  Other: "bg-gray-500",
};

// Well-known account name aliases
const LABEL_ALIASES: Record<string, string> = {
  Deployer: "Deployer",
  "Account 1": "Alice",
  "Account 2": "Bob",
  "Account 3": "Carol",
  "Account 4": "Dave",
  "Account 5": "Eve",
};

/** Display-friendly contract name from internal "V2:factory" format */
function formatContractLabel(name: string): string {
  if (name.startsWith("V2:"))
    return `V2 ${name
      .slice(3)
      .replace(/([A-Z])/g, " $1")
      .trim()}`;
  if (name.startsWith("V3:"))
    return `V3 ${name
      .slice(3)
      .replace(/([A-Z])/g, " $1")
      .trim()}`;
  if (name.startsWith("V4:"))
    return `V4 ${name
      .slice(3)
      .replace(/([A-Z])/g, " $1")
      .trim()}`;
  if (name.startsWith("Foundation:")) return name.slice(11);
  if (name.startsWith("UniswapX:"))
    return name
      .slice(9)
      .replace(/([A-Z])/g, " $1")
      .trim();
  if (name.startsWith("ERC-8004:"))
    return name
      .slice(9)
      .replace(/([A-Z])/g, " $1")
      .trim();
  if (name.startsWith("Vault:"))
    return `Vault ${name
      .slice(6)
      .replace(/([A-Z])/g, " $1")
      .trim()}`;
  return name;
}

/** Format an arg value: resolve addresses to labels, format bigints. */
function formatArgValue(
  value: unknown,
  mergedNames: Record<string, string>,
): string {
  if (typeof value === "bigint") return formatBigIntValue(value);
  if (typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value)) {
    const label = mergedNames[value.toLowerCase()];
    if (label) return `${label} (${value.slice(0, 6)}\u2026${value.slice(-4)})`;
    return `${value.slice(0, 6)}\u2026${value.slice(-4)}`;
  }
  return String(value);
}

function formatArgs(
  args: Record<string, unknown> | null,
  mergedNames: Record<string, string>,
): string {
  if (!args) return "";
  return Object.entries(args)
    .map(([k, v]) => `${k}: ${formatArgValue(v, mergedNames)}`)
    .join(", ");
}

/** Check if a string looks like an address */
function isAddress(v: unknown): v is string {
  return typeof v === "string" && /^0x[0-9a-fA-F]{40}$/.test(v);
}

/** Break hex data into 32-byte words. */
function chunkData(data: string): string[] {
  const hex = data.startsWith("0x") ? data.slice(2) : data;
  if (hex.length === 0) return [];
  const words: string[] = [];
  for (let i = 0; i < hex.length; i += 64) {
    words.push("0x" + hex.slice(i, i + 64));
  }
  return words;
}

// ---------------------------------------------------------------------------
// Copy button wrapper
// ---------------------------------------------------------------------------

function CopyBtn({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <CopyButton
      copied={copied}
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Resolved address cell (matching TransactionLog pattern)
// ---------------------------------------------------------------------------

function ResolvedAddressCell({
  address,
  mergedNames,
  onClickAddress,
  size = 14,
}: {
  address: string;
  mergedNames: Record<string, string>;
  onClickAddress: (addr: string) => void;
  size?: number;
}) {
  const label = mergedNames[address.toLowerCase()] ?? null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClickAddress(address);
      }}
      className="flex max-w-full items-center gap-1 font-mono text-[11px] transition-colors hover:text-[var(--color-gold)]"
      title={address}
    >
      <AccountIdenticon address={address} size={size} />
      <span
        className={cn("truncate", label ? "text-text-dim" : "text-text-muted")}
      >
        {label ?? `${address.slice(0, 6)}\u2026${address.slice(-4)}`}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Event Row (div-based, matching TransactionLog compact layout)
// ---------------------------------------------------------------------------

function EventRow({
  event,
  rowIndex,
  mergedNames,
  onClickAddress,
}: {
  event: DecodedEvent;
  rowIndex: number;
  mergedNames: Record<string, string>;
  onClickAddress: (addr: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const protocol = classifyProtocol(event.contractName);
  const protocolColor = PROTOCOL_ACCENT[protocol] ?? "rgba(255,255,255,0.15)";

  return (
    <>
      {/* Row */}
      <div
        onClick={() => {
          setExpanded(!expanded);
        }}
        className={cn(
          "group relative cursor-pointer border-b border-[var(--glass-border)] transition-colors duration-150",
          "hover:bg-[rgba(201,168,76,0.05)]",
          expanded && "bg-[rgba(201,168,76,0.04)]",
          rowIndex % 2 === 0 && "bg-[rgba(255,255,255,0.01)]",
        )}
      >
        {/* Protocol accent strip */}
        <div
          className="absolute left-0 top-0 h-full w-[3px]"
          style={{ background: protocolColor }}
        />

        {/* Single-row layout */}
        <div className="flex min-h-[40px] flex-wrap items-center gap-x-3 gap-y-1 py-1.5 pl-4 pr-3 sm:flex-nowrap">
          {/* Block / Time */}
          <div className="w-[70px] shrink-0 sm:w-[100px]">
            <div className="text-text-dim font-mono text-[11px] font-medium leading-tight">
              Block {event.blockNumber}
            </div>
            <div className="text-text-muted font-mono text-[10px] leading-tight">
              {formatTimestamp(event.timestamp)}
            </div>
          </div>

          {/* Contract */}
          <div className="hidden shrink-0 overflow-hidden sm:block sm:w-[130px]">
            <ResolvedAddressCell
              address={event.address}
              mergedNames={mergedNames}
              onClickAddress={onClickAddress}
              size={14}
            />
          </div>

          {/* Event Name */}
          <div className="w-[80px] shrink-0 overflow-hidden sm:w-[110px]">
            {event.eventName ? (
              <span
                className={cn(
                  "font-mono text-xs font-medium",
                  eventColorClass(event.eventName),
                )}
              >
                {event.eventName}
              </span>
            ) : (
              <ProtocolBadge protocol={protocol} />
            )}
          </div>

          {/* Parameters (flex) */}
          <div className="text-text-muted min-w-0 flex-1 truncate font-mono text-[10px]">
            {event.args ? formatArgs(event.args, mergedNames) : "\u2014"}
          </div>

          {/* Expand indicator */}
          <div className="text-text-muted w-4 shrink-0 text-center text-[10px]">
            {expanded ? "\u25BE" : "\u25B8"}
          </div>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="border-b border-[var(--glass-border)] bg-[rgba(201,168,76,0.02)]">
          <div className="space-y-3 px-5 py-4 pl-6">
            {/* TX hash + metadata row */}
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <span className="text-text-muted flex items-center gap-1">
                Tx:{" "}
                <code className="text-gold-dim font-mono">
                  {event.transactionHash.slice(0, 10)}&hellip;
                  {event.transactionHash.slice(-8)}
                </code>
                <CopyBtn text={event.transactionHash} />
              </span>
              <span className="text-text-muted">
                Block:{" "}
                <span className="text-text-dim font-mono">
                  {event.blockNumber}
                </span>
              </span>
              <span className="text-text-muted">
                Time:{" "}
                <span className="text-text-dim font-mono">
                  {formatTimestamp(event.timestamp)}
                </span>
              </span>
              <span className="text-text-muted">
                Log Index:{" "}
                <span className="text-text-dim font-mono">
                  {event.logIndex}
                </span>
              </span>
            </div>

            {/* Contract */}
            <div className="text-xs">
              <span className="text-text-muted">Contract: </span>
              <ResolvedAddressCell
                address={event.address}
                mergedNames={mergedNames}
                onClickAddress={onClickAddress}
                size={12}
              />
            </div>

            {/* Decoded parameters */}
            {event.args && (
              <div>
                <div className="text-text-muted mb-1 flex items-center gap-2 text-xs font-semibold">
                  Decoded Parameters
                  <CopyBtn text={formatArgs(event.args, mergedNames)} />
                </div>
                <div className="space-y-1 rounded bg-[rgba(0,0,0,0.2)] p-2">
                  {Object.entries(event.args).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center gap-2 font-mono text-[10px]"
                    >
                      <span className="text-text-muted min-w-[60px]">
                        {key}:
                      </span>
                      {isAddress(value) ? (
                        <ResolvedAddressCell
                          address={value}
                          mergedNames={mergedNames}
                          onClickAddress={onClickAddress}
                          size={12}
                        />
                      ) : (
                        <span className="text-text-dim">
                          {typeof value === "bigint"
                            ? formatBigIntValue(value)
                            : String(value)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw event log (collapsible) */}
            <details>
              <summary className="text-text-muted cursor-pointer text-xs font-semibold hover:text-[var(--color-text-dim)]">
                Raw Event Log
              </summary>
              <pre className="text-text-dim mt-1 max-h-40 overflow-auto rounded bg-[rgba(0,0,0,0.3)] p-2 font-mono text-[10px]">
                {event.topics.map((topic, i) => (
                  <React.Fragment key={i}>
                    {`Topic[${String(i)}]: ${topic}`}
                    {i === 0 && event.eventName
                      ? ` (${event.eventName}(...))`
                      : ""}
                    {"\n"}
                  </React.Fragment>
                ))}
                {event.data !== "0x" && (
                  <>
                    {`Data: ${event.data}`}
                    {"\n"}
                    {chunkData(event.data).map((word, i) => (
                      <React.Fragment key={i}>
                        {`  [word ${String(i)}]: ${word}`}
                        {"\n"}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </pre>
            </details>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

const PROTOCOL_FILTERS = [
  "All",
  "V2",
  "V3",
  "V4",
  "ERC20",
  "ERC-8004",
  "Vault",
  "UniswapX",
  "Other",
] as const;
type ProtocolFilter = (typeof PROTOCOL_FILTERS)[number];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function EventViewer() {
  const { abis, addressNames } = useEventInfra();
  const { deployment } = useDeployment();
  const { events, clear } = useContractEvents({
    abis,
    addressNames,
    fromBlock: deployment?.blockNumber,
  });
  const { labelMap } = useAddressResolver();
  const { accounts } = useEvmBackend();

  const [protocolFilter, setProtocolFilter] = useState<ProtocolFilter>("All");
  const [eventNameFilter, setEventNameFilter] = useState("");
  const [contractFilter, setContractFilter] = useState("All");
  const [fromBlock, setFromBlock] = useState<string>("");
  const [toBlock, setToBlock] = useState<string>("");
  const [clearConfirm, setClearConfirm] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Flip out of loading state once events arrive
  useEffect(() => {
    if (events.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [events.length, isInitialLoad]);

  const isBootstrapping = isInitialLoad && events.length === 0;

  // Merged name map for address resolution
  const mergedNames = useMemo(() => {
    const names: Record<string, string> = { ...labelMap };
    for (const acct of accounts) {
      names[acct.address.toLowerCase()] =
        LABEL_ALIASES[acct.label] ?? acct.label;
    }
    return names;
  }, [accounts, labelMap]);

  // Click an address -> populate search
  const handleAddressClick = useCallback((address: string) => {
    setEventNameFilter(address);
  }, []);

  // Clear with confirmation
  const handleClear = useCallback(() => {
    if (!clearConfirm) {
      setClearConfirm(true);
      clearTimerRef.current = setTimeout(() => {
        setClearConfirm(false);
      }, 3000);
      return;
    }
    setClearConfirm(false);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clear();
  }, [clearConfirm, clear]);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, []);

  // Build contract filter options with human-readable labels
  const contractFilterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const evt of events) {
      if (evt.contractName) names.add(evt.contractName);
    }
    return [
      { label: "All Contracts", value: "All" },
      ...[...names]
        .sort()
        .map((n) => ({ label: formatContractLabel(n), value: n })),
    ];
  }, [events]);

  // Known event names for autocomplete hints
  const knownEventNames = useMemo(() => {
    const names = new Set<string>();
    for (const evt of events) {
      if (evt.eventName) names.add(evt.eventName);
    }
    return [...names].sort();
  }, [events]);

  // Block range from data
  const blockRange = useMemo(() => {
    if (events.length === 0) return { min: 0, max: 0 };
    const first = events[0] as (typeof events)[number];
    let min = first.blockNumber;
    let max = first.blockNumber;
    for (const e of events) {
      if (e.blockNumber < min) min = e.blockNumber;
      if (e.blockNumber > max) max = e.blockNumber;
    }
    return { min, max };
  }, [events]);

  // Apply filters
  const filteredEvents = useMemo(() => {
    let result = events;

    if (protocolFilter !== "All") {
      result = result.filter(
        (e) => classifyProtocol(e.contractName) === protocolFilter,
      );
    }

    if (contractFilter !== "All") {
      result = result.filter((e) => e.contractName === contractFilter);
    }

    if (eventNameFilter.trim()) {
      const q = eventNameFilter.trim().toLowerCase();
      result = result.filter((e) => {
        // Match event name
        if (e.eventName?.toLowerCase().includes(q)) return true;
        // Match address (contract or args)
        if (e.address.toLowerCase().includes(q)) return true;
        // Match tx hash
        if (e.transactionHash.toLowerCase().includes(q)) return true;
        // Match arg values
        if (e.args) {
          for (const v of Object.values(e.args)) {
            if (String(v).toLowerCase().includes(q)) return true;
          }
        }
        return false;
      });
    }

    if (fromBlock.trim()) {
      const from = Number(fromBlock);
      if (!Number.isNaN(from)) {
        result = result.filter((e) => e.blockNumber >= from);
      }
    }

    if (toBlock.trim()) {
      const to = Number(toBlock);
      if (!Number.isNaN(to)) {
        result = result.filter((e) => e.blockNumber <= to);
      }
    }

    // Most recent first
    return result.slice().reverse();
  }, [
    events,
    protocolFilter,
    contractFilter,
    eventNameFilter,
    fromBlock,
    toBlock,
  ]);

  // Stats
  const totalEvents = events.length;
  const blocksWithEvents = new Set(events.map((e) => e.blockNumber)).size;
  const eventsPerBlock =
    blocksWithEvents > 0 ? (totalEvents / blocksWithEvents).toFixed(1) : "0";

  const isFiltered =
    protocolFilter !== "All" ||
    contractFilter !== "All" ||
    eventNameFilter.trim() !== "" ||
    fromBlock.trim() !== "" ||
    toBlock.trim() !== "";

  const clearFilters = useCallback(() => {
    setProtocolFilter("All");
    setContractFilter("All");
    setEventNameFilter("");
    setFromBlock("");
    setToBlock("");
  }, []);

  // Compute counts per protocol filter
  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { All: events.length };
    for (const f of PROTOCOL_FILTERS) {
      if (f === "All") continue;
      counts[f] = events.filter(
        (e) => classifyProtocol(e.contractName) === f,
      ).length;
    }
    return counts;
  }, [events]);

  const mostActiveContract = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) {
      const name = e.contractName ?? e.address;
      counts[name] = (counts[name] ?? 0) + 1;
    }
    let max = 0;
    let maxName = "--";
    for (const [name, count] of Object.entries(counts)) {
      if (count > max) {
        max = count;
        maxName = name;
      }
    }
    return maxName;
  }, [events]);

  // Address of the most active contract for identicon
  const mostActiveAddress = useMemo(() => {
    if (mostActiveContract === "--") return null;
    // If it's an address, use directly. Otherwise find in events.
    if (/^0x[0-9a-fA-F]{40}$/.test(mostActiveContract))
      return mostActiveContract;
    for (const e of events) {
      if (e.contractName === mostActiveContract) return e.address;
    }
    return null;
  }, [mostActiveContract, events]);

  const mostCommonEvent = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) {
      if (e.eventName) counts[e.eventName] = (counts[e.eventName] ?? 0) + 1;
    }
    let max = 0;
    let maxName = "--";
    let maxCount = 0;
    for (const [name, count] of Object.entries(counts)) {
      if (count > max) {
        max = count;
        maxName = name;
        maxCount = count;
      }
    }
    return { name: maxName, count: maxCount };
  }, [events]);

  // Event type distribution (for bar)
  const eventTypeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) {
      const type = classifyEventType(e.eventName);
      counts[type] = (counts[type] ?? 0) + 1;
    }
    if (events.length === 0) return null;
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        count,
        percent: (count / events.length) * 100,
        color: EVENT_TYPE_COLORS[type] ?? "bg-gray-500",
      }));
  }, [events]);

  // Export
  const exportCsv = useCallback(() => {
    const headers = [
      "Block",
      "Time",
      "Contract",
      "Event",
      "Parameters",
      "Tx Hash",
    ];
    const rows = filteredEvents.map((e) => [
      e.blockNumber,
      formatTimestamp(e.timestamp),
      e.contractName ?? e.address,
      e.eventName ?? "",
      e.args ? formatArgs(e.args, mergedNames) : "",
      e.transactionHash,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `events-${String(Date.now())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${String(filteredEvents.length)} events`);
  }, [filteredEvents, mergedNames]);

  const exportJson = useCallback(() => {
    const data = filteredEvents.map((e) => ({
      block: e.blockNumber,
      timestamp: e.timestamp,
      contract: e.contractName ?? e.address,
      event: e.eventName,
      args: e.args,
      transactionHash: e.transactionHash,
      logIndex: e.logIndex,
    }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `events-${String(Date.now())}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${String(filteredEvents.length)} events`);
  }, [filteredEvents]);

  return (
    <div className="animate-page-in">
      {isBootstrapping && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.06)] px-3 py-2 font-mono text-[11px] text-[var(--color-gold)]">
          <Spinner size={12} />
          <span>Loading event history</span>
        </div>
      )}

      <div className="mb-4 flex items-center justify-end gap-2">
        <Tooltip content="Listening for new events">
          <span>
            <LiveIndicator color="var(--color-gold)" />
          </span>
        </Tooltip>
        <Button variant="ghost" size="sm" onClick={handleClear}>
          {clearConfirm ? (
            <span className="text-red-400">Confirm?</span>
          ) : (
            "Clear"
          )}
        </Button>
      </div>

      {/* Summary stat cards */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">Total Events</div>
          <div className="mt-1 font-serif text-xl text-[var(--color-text)]">
            {isBootstrapping ? (
              <div className="skeleton h-7 w-20" />
            ) : (
              <AnimatedBalance value={totalEvents} integer emDash={false} />
            )}
          </div>
          {isFiltered && (
            <div className="text-text-muted mt-0.5 font-mono text-[10px]">
              showing {filteredEvents.length}
            </div>
          )}
        </Glass>
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">Events/Block</div>
          <div className="mt-1 font-serif text-xl text-[var(--color-text)]">
            {isBootstrapping ? (
              <div className="skeleton h-7 w-24" />
            ) : (
              <AnimatedBalance
                value={parseFloat(eventsPerBlock)}
                decimals={1}
                emDash={false}
              />
            )}
          </div>
          <div className="text-text-muted mt-0.5 font-mono text-[10px]">
            {isBootstrapping
              ? "indexing"
              : `across ${String(blocksWithEvents)} blocks`}
          </div>
        </Glass>
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">Most Common</div>
          <div className="mt-1 font-serif text-xl text-[var(--color-text)]">
            {isBootstrapping ? (
              <div className="skeleton h-7 w-28" />
            ) : mostCommonEvent.name !== "--" ? (
              <span className={eventColorClass(mostCommonEvent.name)}>
                {mostCommonEvent.name}
              </span>
            ) : (
              "\u2014"
            )}
          </div>
          {mostCommonEvent.count > 0 && (
            <div
              className="text-text-muted mt-0.5 font-mono text-[10px]"
              data-testid="most-common-value"
            >
              {mostCommonEvent.count} occurrences
            </div>
          )}
        </Glass>
      </div>

      {/* Event type distribution bar */}
      {eventTypeDistribution && (
        <div className="mb-5">
          <div className="mb-1.5 flex items-center justify-between gap-4">
            <span className="hero-stat-label shrink-0">Events by type</span>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
              {eventTypeDistribution.map((seg) => (
                <span
                  key={seg.type}
                  className="flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]"
                >
                  <span
                    className={cn(
                      "inline-block h-2 w-2 rounded-full",
                      seg.color,
                    )}
                  />
                  {seg.type}{" "}
                  <span className="text-text-dim">
                    {seg.percent.toFixed(0)}%
                  </span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.04)]">
            {eventTypeDistribution.map((seg) => (
              <div
                key={seg.type}
                className={cn("h-full transition-all duration-500", seg.color)}
                style={{ width: `${String(seg.percent)}%` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Insight row */}
      <Glass className="mb-5 flex flex-wrap items-center gap-6 px-4 py-2">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-text-muted font-mono">Most Active:</span>
          {mostActiveAddress && (
            <AccountIdenticon address={mostActiveAddress} size={14} />
          )}
          <span
            className="text-text-dim truncate font-mono"
            data-testid="most-active-value"
          >
            {formatContractLabel(mostActiveContract)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-text-muted font-mono">Block Range:</span>
          <span className="text-text-dim font-mono">
            {events.length > 0
              ? `${String(blockRange.min)} \u2192 ${String(blockRange.max)}`
              : "\u2014"}
          </span>
        </div>
      </Glass>

      {/* Protocol filter tabs */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {PROTOCOL_FILTERS.map((f) => {
          const count = filterCounts[f] ?? 0;
          const isZero = f !== "All" && count === 0;
          return (
            <button
              key={f}
              type="button"
              onClick={() => {
                if (!isZero) setProtocolFilter(f);
              }}
              className={cn(
                "rounded-full px-3.5 py-1.5 font-mono text-[10px] transition-all duration-200",
                protocolFilter === f
                  ? "bg-[var(--color-gold)] text-black shadow-[0_0_12px_rgba(201,168,76,0.2)]"
                  : "text-text-muted hover:text-gold border border-[var(--glass-border)] bg-transparent hover:border-[rgba(201,168,76,0.3)]",
                isZero && "pointer-events-none opacity-25",
              )}
              disabled={isZero}
            >
              {f} (<AnimatedBalance value={count} integer emDash={false} />)
            </button>
          );
        })}

        {isFiltered && (
          <button
            type="button"
            onClick={clearFilters}
            className="btn-ghost h-auto px-2.5 py-1.5"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Search + filters + export */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          options={contractFilterOptions}
          value={contractFilter}
          onValueChange={setContractFilter}
          className="w-full sm:w-44"
        />

        <Input
          type="number"
          placeholder={
            events.length > 0
              ? `From (${String(blockRange.min)})`
              : "From Block"
          }
          value={fromBlock}
          onChange={(e) => {
            setFromBlock(e.target.value);
          }}
          className="w-20 sm:w-28"
        />
        <Input
          type="number"
          placeholder={
            events.length > 0 ? `To (${String(blockRange.max)})` : "To Block"
          }
          value={toBlock}
          onChange={(e) => {
            setToBlock(e.target.value);
          }}
          className="w-20 sm:w-28"
        />

        <button type="button" onClick={exportCsv} className="chip-preset">
          CSV
        </button>
        <button type="button" onClick={exportJson} className="chip-preset">
          JSON
        </button>

        <div className="flex-1" />

        <div className="relative">
          <svg
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search events..."
            value={eventNameFilter}
            onChange={(e) => {
              setEventNameFilter(e.target.value);
            }}
            data-testid="event-search"
            className="text-text-dim placeholder-text-muted w-full rounded-full border border-[var(--glass-border)] bg-[rgba(255,255,255,0.03)] py-1.5 pl-8 pr-3 font-mono text-xs outline-none transition-colors focus:border-[var(--color-gold)] sm:w-56"
            list="event-names"
          />
        </div>
        <datalist id="event-names">
          {knownEventNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      </div>

      {/* Event feed */}
      <Glass className="p-0">
        <div
          ref={scrollRef}
          className="max-h-[calc(100vh-480px)] min-h-[200px] overflow-y-auto"
        >
          {isBootstrapping ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 8 }, (_, i) => i).map((i) => (
                <div
                  key={i}
                  className="skeleton skeleton-row rounded"
                  style={{ animationDelay: `${String(i * 80)}ms` }}
                />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="py-16">
              <EmptyState message="No events yet. Mine blocks or submit transactions to see events here." />
            </div>
          ) : (
            filteredEvents.map((event, i) => {
              const prevBlock = filteredEvents[i - 1]?.blockNumber ?? null;
              const isNewBlock =
                i > 0 && prevBlock !== null && prevBlock !== event.blockNumber;
              return (
                <React.Fragment
                  key={`${event.transactionHash}-${String(event.logIndex)}-${String(i)}`}
                >
                  {isNewBlock && (
                    <div className="border-t border-t-[rgba(255,255,255,0.06)]" />
                  )}
                  <EventRow
                    event={event}
                    rowIndex={i}
                    mergedNames={mergedNames}
                    onClickAddress={handleAddressClick}
                  />
                </React.Fragment>
              );
            })
          )}
        </div>
      </Glass>
    </div>
  );
}
